<script lang="ts">
    import tabIcon from "@/assets/operationskoordinator.svg";

    export default {
        name: "Operationskoordinering",
        tabName: "Operationskoordinering",
        icon: tabIcon,
        refreshTab: true,
    };
</script>

<script setup lang="ts">
    import { CellContentAlignment, CellContentWidth } from "@/components/KTable/helpers";
    import KTable from "@/components/KTable/KTable.vue";
    import KallelserSmsButton from "@/tabs/Operation/Operationskoordinering/KallelserSmsButton.vue";
    import CellInfoOpplan from "@/tabs/Operation/Operationskoordinering/OperationskoordineringTable/CellInfoOpplan.vue";
    import CellPersonId from "@/tabs/Operation/Operationskoordinering/OperationskoordineringTable/CellPersonId.vue";
    import CellPreanestesibedomning from "@/tabs/Operation/Operationskoordinering/OperationskoordineringTable/CellPreanestesibedomning.vue";
    import CellRemisser from "@/tabs/Operation/Operationskoordinering/OperationskoordineringTable/CellRemisser.vue";
    import CellStatus from "@/tabs/Operation/Operationskoordinering/OperationskoordineringTable/CellStatus.vue";
    import CellPreliminarbokning from "@/tabs/Operation/Operationskoordinering/OperationskoordineringTable/CellPreliminarbokning.vue";
    import OperationskoordineringFilter from "@/tabs/Operation/OperationskoordineringFilter.vue";
    import { ref, onMounted, computed, onUnmounted } from "vue";
    import { openDialog } from "@/utils";
    import { preliminarbokningLocked } from "@/tabs/Operation/utilsTs";
    import { useStore } from "@/store";
    import ActionIconEdit from "@/assets/actionIcons/edit.svg";
    import ActionIconAvvakta from "@/assets/actionIcons/avvakta.svg";
    import ActionIconShow from "@/assets/actionIcons/show.svg";
    import ActionIconBokaoperation from "@/assets/actionIcons/bokaoperation.svg";
    import ActionIconAvboka from "@/assets/actionIcons/avboka.svg";
    import ActionIconSend from "@/assets/actionIcons/send.svg";
    import ActionIconSms from "@/assets/actionIcons/sms.svg";
    import ActionIconSkapapreanestesibedomning from "@/assets/actionIcons/skapapreanestesibedomning.svg";
    import ActionIconOpanmalan from "@/assets/actionIcons/opanmalan.svg";
    import Operationsanmalan from "@/tabs/Operation/Operationsanmalan.vue";
    import dialogEvents from "@/dialogEvents";
    import AvvaktaOperation from "@/tabs/Operation/AvvaktaOperation.vue";
    import OrsakTillAvbokningOchStrykning from "@/tabs/Operation/OrsakTillAvbokningOchStrykning.vue";
    import OrsakTillAvvaktan from "@/tabs/Operation/OrsakTillAvvaktan.vue";
    import BokaOperation from "@/tabs/Operation/BokaOperation.vue";
    import AvbokaOperation from "@/tabs/Operation/AvbokaOperation.vue";
    import KallelseSend from "@/tabs/Operation/Kallelser/KallelseSend.vue";
    import SMSSend from "@/tabs/Operation/SMS/SMSSend.vue";
    import Preanestesibedomning from "@/tabs/Operation/Preanestesibedomning.vue";
    import PreanestesibedomningVisa from "@/tabs/Operation/PreanestesibedomningVisa.vue";
    import Operationsinformation from "@/tabs/Operation/Operationsinformation.vue";
    import CellKallelseSms from "@/components/Table/CellKallelseSMS.vue";
    import emitter from "tiny-emitter/instance";
    import { Column, KTableAction, TableValue } from "@/components/KTable/types";
    import { Operationsanmalan as OperationsanmalanType } from "@/store/modules/operation/operationsanmalanModule";
    import ActionIconRemiss from "@/assets/actionIcons/remiss.svg";
    import SkickaSomRemiss from "@/tabs/Operation/SkickaSomRemiss.vue";
    import { OperationConfigModuleName } from "@/store/modules/types/configModuleTypes";

    const store = useStore();

    // Destructuring på props tar bort dess reaktivitet i Vue2. This is as intended
    // eslint-disable-next-line vue/no-setup-props-destructure
    const { tabId, initialFilterParams } = defineProps<{
        tabId: number;
        initialFilterParams?: Record<string, unknown>;
    }>();

    const filterRef = ref();
    const skickaSomRemissRef = ref();

    const columns: Column[] = [
        {
            field: "personId",
            header: "PersonID",
            sortField: "patient__personId",
            frozen: true,
            alignFrozen: "left",
            bodyTemplate: CellPersonId,
            limitWidth: true,
            minWidth: CellContentWidth.custom({ amount: 8 }),
            alignContent: CellContentAlignment.Center,
            sortable: true,
        },
        {
            field: "patientnamn",
            header: "Patientnamn",
            sortField: "patient__namn",
            sortable: true,
        },
        {
            field: "vantelistdatum",
            header: "Väntelistdatum",
            contentType: "date",
            sortField: "documentTime",
            sortable: true,
        },
        {
            field: "opnr",
            header: "Operationsnummer",
            sortField: "opnr",
            sortable: true,
        },
        {
            field: "prioritet",
            header: "Prioritet",
            sortField: "prio__ordning",
            sortable: true,
        },
        {
            field: "sida",
            header: "Sida",
            sortField: "lateralitet",
            sortable: true,
        },
        {
            field: "kortVarsel",
            header: "Kort varsel",
            sortField: "kort_varsel",
            sortable: true,
            contentType: "boolean",
        },
        {
            field: "onskatOpdatum",
            header: "Önskat opdatum",
            contentType: "date",
            sortField: "preliminart_datum",
            sortable: true,
        },
        {
            field: "operationsenhet",
            header: "Operationsenhet",
            sortField: "operationsenhet",
            sortable: true,
        },
        {
            field: "operator",
            header: "Operatör",
            sortField: "current_bokad_huvudoperator",
            sortable: true,
        },
        {
            field: "ass_operatorer",
            header: "Ass. operatör",
            contentType: "array",
            contentDirection: "vertical",
        },
        {
            field: "optid",
            header: "Optid(min)",
            sortField: "optid",
            sortable: true,
        },
        {
            field: "infoOpplan",
            header: "Info opplan",
            sortField: "info_till_opplaneringen",
            bodyTemplate: CellInfoOpplan,
            truncate: true,
            sortable: true,
        },
        {
            field: "typoperation",
            header: "Typoperation",
            sortField: "typoperation__namn",
            sortable: true,
        },
        {
            field: "huvudoperationskod",
            header: "Huvudoperationskod",
            sortField: "huvudoperationskod",
            sortable: true,
        },
        {
            field: "kontaktorsak",
            header: "Kontaktorsak",
            sortField: "kontaktorsak__displayName",
            sortable: true,
        },
        {
            field: "vardform",
            header: "Vårdform",
            sortField: "planerad_vardform",
            sortable: true,
        },
        {
            field: "anestesiform",
            header: "Anestesiform",
            sortField: "anestesi__displayName",
            sortable: true,
        },
        {
            field: "avtal",
            header: "Avtal",
            sortField: "avtal__namn",
            sortable: true,
        },
        {
            field: "bokatOpdatum",
            header: "Bokat opdatum",
            sortField: "bokad_operation_datum",
            contentType: "date",
            sortable: true,
        },
        {
            field: "bokadSal",
            header: "Bokad sal",
            sortField: "bokad_sal__ordning",
            sortable: true,
        },
        {
            field: "kallelserOchSms",
            header: "Kallelser och sms",
            bodyTemplate: CellKallelseSms,
        },
        {
            field: "preanestesibedomning",
            header: "Preanestesibedömning",
            sortField: "preanestesibedomning_godkannande__sortering",
            bodyTemplate: CellPreanestesibedomning,
            limitWidth: true,
            minWidth: CellContentWidth.m,
            sortable: true,
        },
        {
            field: "studieKvalitetsregister",
            header: "Studie/Kvalitetsregister",
            sortable: false,
            contentType: "array",
            contentDirection: "vertical",
        },
        {
            field: "datum_rontgenbilder_till_op",
            header: "Datum för röntgenbilder till op",
            sortField: "datum_rontgenbilder_till_op",
            sortable: true,
            contentType: "date",
        },
        {
            field: "skickadeRemisser",
            header: "Mottagare",
            bodyTemplate: CellRemisser,
            shouldRender: () => {
                return operationRemiss.value;
            },
        },
        {
            field: "mottagnaRemisser",
            header: "Avsändare",
            bodyTemplate: CellRemisser,
            shouldRender: () => {
                return operationRemiss.value;
            },
        },
        {
            field: "preliminarbokning",
            header: "Preliminärbokad",
            bodyTemplate: CellPreliminarbokning,
            sortField: "preliminarbokning",
            sortable: true,
        },
        {
            field: "status",
            header: "Status",
            sortField: "status__sortering",
            frozen: true,
            alignFrozen: "right",
            bodyTemplate: CellStatus,
            limitWidth: true,
            minWidth: CellContentWidth.custom({ amount: 14 }),
            sortable: true,
        },
    ];

    const dropdownActions: KTableAction[] = [
        {
            title: "Redigera operationsanmälan",
            icon: ActionIconEdit,
            show: (item) => {
                const typedItem = item as {
                    status: {
                        status: string;
                    };
                };

                return (
                    [
                        "Att hantera",
                        "Väntar på preanestesibedömning",
                        "Prioriterad preanestesibedömning",
                        "Väntar på viktnedgång",
                        "Väntar på rökstopp",
                        "Väntar på prover/undersökning",
                        "Offertförfrågan skickad",
                        "Preanestesibedömning genomförd",
                        "Preanestesibedömning prio genomförd",
                        "Klar för operationsbokning",
                        "Bokad",
                        "Avbokad",
                        "Patientvald väntan",
                        "Medicinsk väntan",
                        "Administrativ väntan",
                        "Pågående",
                        "Väntar på hälsodeklaration",
                        "Offertförfrågan ej skickad",
                        "Betalningsförbindelse skickad",
                    ].includes(typedItem.status.status) &&
                    store.getters["ability/can"]({ action: "update", subject: "anmalan" })
                );
            },
            callback: ({ data }) => {
                const anmalan = store.getters["operation/operationsanmalanV2/operationsanmalanById"]({
                    id: (data as { id: string }).id,
                });

                store.dispatch("operation/operationsanmalan/openOpAnmalan", {
                    component: Operationsanmalan,
                    id: anmalan.id,
                    patient: anmalan.patient,
                    mode: "opcoordinator",
                });
            },
        },
        {
            title: "Avvakta operationen",
            icon: ActionIconAvvakta,
            show: (item) => {
                const typedItem = item as {
                    status: {
                        status: string;
                    };
                };

                return (
                    [
                        "Att hantera",
                        "Väntar på viktnedgång",
                        "Väntar på rökstopp",
                        "Väntar på prover/undersökning",
                        "Offertförfrågan skickad",
                        "Klar för operationsbokning",
                        "Avbokad",
                        "Preanestesibedömning genomförd",
                        "Preanestesibedömning prio genomförd",
                        "Väntar på hälsodeklaration",
                        "Offertförfrågan ej skickad",
                        "Betalningsförbindelse skickad",
                    ].includes(typedItem.status.status) &&
                    store.getters["ability/can"]({
                        action: "avboka",
                        subject: "anmalan",
                    })
                );
            },
            callback: ({ data }) => {
                dialogEvents.$emit("openPopup", {
                    title: "Avvakta operation",
                    component: AvvaktaOperation,
                    dismissable: true,
                    data: {
                        listItem: store.getters["operation/operationsanmalanV2/operationsanmalanById"]({
                            id: (data as { id: string }).id,
                        }),
                    },
                    allowScroll: true,
                });
            },
        },
        {
            title: "Visa orsak till avvaktan",
            icon: ActionIconShow,
            show: (item) => {
                const typedItem = item as {
                    status: {
                        status: string;
                    };
                };

                return (
                    ["Patientvald väntan", "Medicinsk väntan", "Administrativ väntan"].includes(
                        typedItem.status.status
                    ) &&
                    store.getters["ability/can"]({
                        action: "avvakta",
                        subject: "anmalan",
                    })
                );
            },
            callback: ({ data }) => {
                dialogEvents.$emit("openPopup", {
                    data: store.getters["operation/operationsanmalanV2/operationsanmalanById"]({
                        id: (data as { id: string }).id,
                    }),
                    title: "Orsak till avvaktan",
                    component: OrsakTillAvvaktan,
                    dismissable: true,
                });
            },
        },
        {
            title: "Boka operationen",
            icon: ActionIconBokaoperation,
            show: (item) => {
                const typedItem = item as {
                    status: {
                        status: string;
                        preliminarbokning: boolean;
                        huvudoperator: string;
                    };
                };

                return (
                    [
                        "Klar för operationsbokning",
                        "Avbokad",
                        "Väntar på preanestesibedömning",
                        "Prioriterad preanestesibedömning",
                        "Preanestesibedömning genomförd",
                        "Preanestesibedömning prio genomförd",
                    ].includes(typedItem.status.status) &&
                    !typedItem.status.preliminarbokning &&
                    !preliminarbokningLocked(typedItem.status) &&
                    store.getters["ability/can"]({
                        action: "boka",
                        subject: "anmalan",
                    })
                );
            },
            callback: ({ data }) => {
                const typedData = data as { id: string };
                bokaOperation(typedData);
            },
        },
        {
            title: "Redigera bokningen",
            icon: ActionIconEdit,
            show: (item) => {
                const typedItem = item as {
                    status: {
                        status: string;
                        preliminarbokning: boolean;
                        huvudoperator: string;
                    };
                };

                return (
                    (["Bokad"].includes(typedItem.status.status) ||
                        (typedItem.status.preliminarbokning && !preliminarbokningLocked(typedItem.status))) &&
                    store.getters["ability/can"]({ action: "boka", subject: "anmalan" })
                );
            },
            callback: ({ data }) => {
                const typedData = data as { id: string };
                bokaOperation(typedData, "Redigera bokning");
            },
        },
        {
            title: "Preliminärboka operation",
            icon: ActionIconBokaoperation,
            show: (item) => {
                const typedItem = item as {
                    status: {
                        status: string;
                        preliminarbokning: boolean;
                        huvudoperator: string;
                    };
                };

                return (
                    !typedItem.status.preliminarbokning &&
                    preliminarbokningLocked(typedItem.status) &&
                    store.getters["ability/can"]({ action: "boka", subject: "anmalan" })
                );
            },
            callback: ({ data }) => {
                const typedData = data as { id: string };
                bokaOperation(typedData, "Preliminärboka operation", true);
            },
        },
        {
            title: "Redigera preliminärbokning",
            icon: ActionIconBokaoperation,
            show: (item) => {
                const typedItem = item as {
                    status: {
                        status: string;
                        preliminarbokning: boolean;
                        huvudoperator: string;
                    };
                };

                return (
                    typedItem.status.preliminarbokning &&
                    preliminarbokningLocked(typedItem.status) &&
                    store.getters["ability/can"]({ action: "boka", subject: "anmalan" })
                );
            },
            callback: ({ data }) => {
                const typedData = data as { id: string };
                bokaOperation(typedData, "Redigera preliminärbokning", true);
            },
        },
        {
            title: "Ta bort preliminärbokning",
            icon: ActionIconAvboka,
            show: (item) => {
                const typedItem = item as {
                    status: {
                        status: string;
                        preliminarbokning: boolean;
                        huvudoperator: string;
                    };
                };

                return (
                    typedItem.status.preliminarbokning &&
                    store.getters["ability/can"]({ action: "avboka", subject: "anmalan" })
                );
            },
            callback: ({ data }) => {
                const typedData = data as { id: string };
                store.dispatch("operation/operationsanmalan/taBortPreliminarbokningById", typedData.id);
            },
        },
        {
            title: "Avboka eller Stryk operationen",
            icon: ActionIconAvboka,
            show: (item) => {
                const typedItem = item as {
                    status: {
                        status: string;
                    };
                };

                return (
                    [
                        "Att hantera",
                        "Väntar på preanestesibedömning",
                        "Prioriterad preanestesibedömning",
                        "Väntar på viktnedgång",
                        "Väntar på rökstopp",
                        "Väntar på prover/undersökning",
                        "Offertförfrågan skickad",
                        "Klar för operationsbokning",
                        "Bokad",
                        "Avbokad",
                        "Pågående",
                        "Patientvald väntan",
                        "Medicinsk väntan",
                        "Administrativ väntan",
                        "Preanestesibedömning genomförd",
                        "Preanestesibedömning prio genomförd",
                        "Väntar på hälsodeklaration",
                        "Offertförfrågan ej skickad",
                        "Betalningsförbindelse skickad",
                    ].includes(typedItem.status.status) &&
                    store.getters["ability/can"]({ action: "boka", subject: "anmalan" })
                );
            },
            callback: ({ data }) => {
                dialogEvents.$emit("openPopup", {
                    title: "Avboka eller stryk operation",
                    component: AvbokaOperation,
                    dismissable: true,
                    data: {
                        listItem: store.getters["operation/operationsanmalanV2/operationsanmalanById"]({
                            id: (data as { id: string }).id,
                        }),
                    },
                    allowScroll: true,
                });
            },
        },
        {
            title: "Visa orsak till avbokning och strykning",
            icon: ActionIconShow,
            show: (item) =>
                ["Avbokad", "Struken"].includes(
                    (
                        item as {
                            status: {
                                status: string;
                            };
                        }
                    ).status.status
                ) &&
                store.getters["ability/can"]({
                    action: "read",
                    subject: "operationskoordinering",
                }),
            callback: ({ data }) => {
                dialogEvents.$emit("openPopup", {
                    data: store.getters["operation/operationsanmalanV2/operationsanmalanById"]({
                        id: (data as { id: string }).id,
                    }),
                    title: "Orsak till avbokning och strykning",
                    component: OrsakTillAvbokningOchStrykning,
                    dismissable: true,
                });
            },
        },
        {
            title: "Skapa kallelse",
            icon: ActionIconSend,
            show: (item) => {
                const typedItem = item as {
                    status: {
                        status: string;
                        preliminarbokning: boolean;
                    };
                };

                return (
                    (typedItem.status.status === "Bokad" || typedItem.status.preliminarbokning) &&
                    store.getters["ability/can"]({ action: "kallelse", subject: "anmalan" })
                );
            },
            callback: ({ data }) => {
                store.dispatch("tabs/openTab", {
                    component: KallelseSend,
                    parameters: {
                        receiver: store.getters["operation/operationsanmalanV2/operationsanmalanById"]({
                            id: (data as { id: string }).id,
                        }),
                        baseUrl: "/operation/anmalan/",
                        path: "/kallelse/",
                    },
                });
            },
        },
        {
            title: "Skapa sms",
            icon: ActionIconSms,
            show: (item) => {
                const anmalan = store.getters["operation/operationsanmalanV2/operationsanmalanById"]({
                    id: (item as { id: string }).id,
                });

                return (
                    (anmalan.status === "Bokad" || anmalan.preliminarbokning) &&
                    anmalan.patient_display?.onskar_sms === true &&
                    store.getters["ability/can"]({ action: "sms", subject: "anmalan" })
                );
            },
            callback: ({ data }) => {
                store.dispatch("tabs/openTab", {
                    component: SMSSend,
                    parameters: {
                        receivers: [
                            store.getters["operation/operationsanmalanV2/operationsanmalanById"]({
                                id: (data as { id: string }).id,
                            }),
                        ],
                        baseUrl: "/operation/anmalan/",
                        path: "/sms/",
                    },
                });
            },
        },
        {
            title: "Skapa preanestesibedömning",
            icon: ActionIconSkapapreanestesibedomning,
            show: (item) => {
                const typedItem = item as {
                    id: string;
                    preanestesibedomning: string;
                };

                const anmalan = store.getters["operation/operationsanmalanV2/operationsanmalanById"]({
                    id: typedItem.id,
                });

                return (
                    !anmalan.aktuell_preanestesibedomning &&
                    typedItem.preanestesibedomning === "Att göra" &&
                    store.getters["ability/can"]({
                        action: "create",
                        subject: "preanestesibedomning",
                    })
                );
            },
            callback: ({ data }) => {
                const anmalan = store.getters["operation/operationsanmalanV2/operationsanmalanById"]({
                    id: (data as { id: string }).id,
                });

                store.dispatch("tabs/openTab", {
                    component: Preanestesibedomning,
                    parameters: {
                        operationsanmalanId: anmalan.id,
                        preanestesibedomningId: anmalan.aktuell_preanestesibedomning,
                    },
                });
            },
        },
        {
            title: "Redigera preanestesibedömning",
            icon: ActionIconSkapapreanestesibedomning,
            show: (item) => {
                const userRole = store.getters["userData/currentProfile"]?.roll?.code;

                const anmalan = store.getters["operation/operationsanmalanV2/operationsanmalanById"]({
                    id: (item as { id: string }).id,
                });

                return (
                    anmalan.aktuell_preanestesibedomning &&
                    userRole === "anestesilakare" &&
                    store.getters["ability/can"]({ action: "create", subject: "preanestesibedomning" })
                );
            },
            callback: ({ data }) => {
                const anmalan = store.getters["operation/operationsanmalanV2/operationsanmalanById"]({
                    id: (data as { id: string }).id,
                });

                store.dispatch("tabs/openTab", {
                    component: Preanestesibedomning,
                    parameters: {
                        operationsanmalanId: anmalan.id,
                        preanestesibedomningId: anmalan.aktuell_preanestesibedomning,
                        showPreanestesibedomning: true,
                    },
                });
            },
        },
        {
            title: "Visa preanestesibedömning",
            icon: ActionIconShow,
            show: (item) => {
                const anmalan = store.getters["operation/operationsanmalanV2/operationsanmalanById"]({
                    id: (item as { id: string }).id,
                });

                return (
                    anmalan.aktuell_preanestesibedomning &&
                    store.getters["ability/can"]({ action: "read", subject: "preanestesibedomning" })
                );
            },
            callback: ({ data }) => {
                const anmalan = store.getters["operation/operationsanmalanV2/operationsanmalanById"]({
                    id: (data as { id: string }).id,
                });

                store.dispatch("tabs/openTab", {
                    component: PreanestesibedomningVisa,
                    parameters: {
                        operationsanmalanId: anmalan.id,
                        preanestesibedomningId: anmalan.aktuell_preanestesibedomning,
                    },
                });
            },
        },
        {
            title: "Visa operationsinformation",
            icon: ActionIconOpanmalan,
            show: () =>
                store.getters["ability/can"]({
                    action: "read",
                    subject: "anmalan",
                }),
            callback: ({ data }) => {
                store.dispatch("tabs/openTab", {
                    component: Operationsinformation,
                    parameters: { operationsanmalan_id: (data as { id: string }).id },
                });
            },
        },
        {
            title: "Skicka anmälan",
            icon: ActionIconRemiss,
            show: (item) => {
                const anmalan = store.getters["operation/operationsanmalanV2/operationsanmalanById"]({
                    id: (item as { id: string }).id,
                });

                return (
                    ["Att hantera"].includes(anmalan.status) &&
                    store.getters["ability/can"]({ action: "remiss", subject: "anmalan" }) &&
                    operationRemiss.value
                );
            },
            callback: ({ data }) => {
                const typedData = data as { id: string };

                skickaSomRemissRef.value.show({ id: typedData.id });
            },
        },
    ];

    const listPage = ref<number>(1);
    const listCount = ref<number>(0);
    const listLimit = ref<number>(
        JSON.parse(sessionStorage.getItem("operationskoordinering-table") ?? "{}")?.rows ?? 20
    );
    const operationskoordineringDefaultFilter = computed<{ Operationsenhet?: boolean } | undefined>(
        () => store.state.config?.operation?.[OperationConfigModuleName.OperationskoordineringDefaultFilter]
    );
    const defaultOperationsenhetFilter = computed<{ operationsenhet?: { value: unknown; label: string } }>(() => {
        const shouldFilterOperationsenhet = operationskoordineringDefaultFilter.value?.Operationsenhet;
        const currentProfile = store.getters["userData/currentProfile"];
        const currentVardenhetstyp = currentProfile?.orgUnit?.vardenhetstyp;
        if (shouldFilterOperationsenhet && currentVardenhetstyp === "operationsenhet") {
            return {
                operationsenhet: {
                    value: currentProfile?.orgUnit?.id,
                    label: currentProfile?.orgUnit?.name,
                },
            };
        } else {
            return {};
        }
    });
    const filterParams = ref<{
        [key: string]: string | boolean | { value: unknown; label: string };
    }>({
        show_strukna: false,
        ...defaultOperationsenhetFilter.value,
        ...initialFilterParams,
    });
    const isLoading = ref(false);
    const subsetName = ref<string>("default");
    const first = computed<number>(() => {
        return (listPage.value - 1) * listLimit.value;
    });
    const operationRemiss = computed<boolean>(() => store.state.config.operation?.OPERATION_REMISS ?? false);
    const operationsanmalningar = computed<OperationsanmalanType[]>(() => {
        const subset = store.getters["operation/operationsanmalanV2/operationsanmalanSubset"]({
            subsetName: subsetName.value,
            offset: listLimit.value * (listPage.value - 1),
            limit: listLimit.value,
        });
        const newlyUpdated = store.getters["operation/operationsanmalanV2/operationsanmalanSubset"]({
            subsetName: "newlyUpdated",
            toArray: false,
            limit: 0,
            offset: 0,
        });
        const newlyCreated = store.getters["operation/operationsanmalanV2/operationsanmalanSubset"]({
            subsetName: "newlyCreated",
            toArray: false,
            limit: 0,
            offset: 0,
        });

        const mappedSubset = subset.map((anmalan: OperationsanmalanType) => {
            return {
                id: anmalan.id,
                personId: {
                    personId: anmalan.patient_display?.personId_formatted,
                    salstid: anmalan.salstid,
                    id: anmalan.id,
                    current_bokad_huvudoperator_display: anmalan.current_bokad_huvudoperator_display,
                    forberedelser: anmalan.forberedelser,
                    optid: anmalan.optid,
                    avveckling: anmalan.avveckling,
                    status: anmalan.status,
                },
                patientnamn: anmalan.patient_display?.namn_reverse,
                vantelistdatum: anmalan.documentTime,
                opnr: anmalan.opnr,
                prioritet: anmalan.prio_display?.displayName,
                sida: anmalan.lateralitet_display,
                kortVarsel: anmalan.kort_varsel,
                onskatOpdatum: anmalan.preliminart_datum,
                operator: anmalan.current_bokad_huvudoperator_display?.user_display.name ?? "Ej bestämd",
                ass_operatorer: anmalan.ass_operatorer_display?.map((assop: any) => assop.user_display.name),
                operationsenhet: anmalan.operationsenhet_display?.name,
                optid: anmalan.optid,
                typoperation: anmalan.typoperation_display?.namn,
                huvudoperationskod: `${anmalan.huvudoperationskod_display?.code} ${anmalan.huvudoperationskod_display?.displayName}`,
                vardform: anmalan.planerad_vardform_display?.namn,
                anestesiform: anmalan.anestesi_display?.displayName,
                avtal: anmalan.avtal_display?.namn,
                bokatOpdatum: anmalan.bokad_operation_datum,
                bokadSal: anmalan.bokad_sal_display?.namn,
                preanestesibedomning: anmalan.preanestesibedomning_godkannande,
                status: {
                    status: anmalan.status,
                    id: anmalan.id,
                    huvudoperator: anmalan.current_bokad_huvudoperator,
                    preliminarbokning: anmalan.preliminarbokning,
                },
                patient: anmalan.patient,
                infoOpplan: {
                    undantagskommentar: anmalan.undantagskommentar,
                    tolk: anmalan.tolk,
                    diabetes: anmalan.diabetes,
                    infoTillOpplaneringen: anmalan.info_till_opplaneringen,
                    infoTillKoordineringOchOperation: anmalan.info_till_koordinering_och_operation,
                    preanestesibedomningInformationTillKoordinator:
                        anmalan.preanestesibedomning_information_till_koordinator,
                },
                kontaktorsak: anmalan.kontaktorsak_display?.displayName,
                kallelserOchSms: {
                    kallelseStatus: anmalan.kallelse_status,
                    smsStatus: anmalan.sms_status,
                    status: anmalan.status,
                    preliminarbokning: anmalan.preliminarbokning,
                },
                studieKvalitetsregister: anmalan.studie_kvalitetsregister_display?.map((item) => item.namn),
                datum_rontgenbilder_till_op: anmalan.datum_rontgenbilder_till_op,
                skickadeRemisser: anmalan.skickade_remisser?.map((remiss) => remiss.mottagare_display.namn),
                mottagnaRemisser: anmalan.mottagna_remisser?.map((remiss) => remiss.avsandare_display.namn),
                tableInfo: {
                    isNewlyUpdated: !!newlyUpdated.get(anmalan.id),
                    isNewlyCreated: !!newlyCreated.get(anmalan.id),
                },
                preliminarbokning: anmalan.preliminarbokning,
            };
        });

        return mappedSubset;
    });

    const fetchOperationsanmalningar = async ({
        page,
        forceRefresh,
        resetLocalSubsets,
        limit = listLimit.value,
    }: {
        page: number;
        limit?: number;
        forceRefresh?: boolean;
        resetLocalSubsets?: boolean;
    }) => {
        isLoading.value = true;

        try {
            const {
                total,
                results,
                subsetName: fetchedSubsetName,
            } = await store.dispatch("operation/operationsanmalanV2/fetchOperationsanmalningar", {
                page,
                limit,
                forceRefresh,
                resetLocalSubsets,
                storeInfo: {
                    partial: true,
                },
                apiUrl: "operation/koordinering/",
                filterParams: filterParams?.value,
            });

            isLoading.value = false;
            subsetName.value = fetchedSubsetName;

            if (typeof total === "number") {
                listCount.value = total;
            }

            listPage.value = page;

            if (!forceRefresh && !results) {
                // Gör en hämtning i bakgrunden för att se till att data är up to date, tills vi har sockets
                const { total: backgroundTotal } = await store.dispatch(
                    "operation/operationsanmalanV2/fetchOperationsanmalningar",
                    {
                        page,
                        limit,
                        storeInfo: {
                            partial: true,
                        },
                        apiUrl: "operation/koordinering/",
                        forceRefresh: true,
                        filterParams: filterParams?.value,
                    }
                );

                if (typeof total === "number" && listCount.value !== backgroundTotal) {
                    listCount.value = backgroundTotal;
                }
            }
        } catch (error) {
            isLoading.value = false;

            console.error(error);
            openDialog("Kunde inte hämta operationsanmälningar", "error");
        }
    };

    const onRowClick = ({ data }: { data: TableValue }) => {
        if (data.patient) {
            store.dispatch("patientData/loadFromBackend", {
                id: data.patient,
                failSilently: false,
            });
        }
    };

    const onVisaStrukna = async ({ value }: { value: boolean }) => {
        filterRef?.value?.setFieldValue?.({
            value,
            name: "show_strukna",
        });

        const data = await filterRef?.value?.manualSubmit?.();

        if (data) {
            onFilter({ value: data });
        }
    };

    const bokaOperation = (data: { id: string }, title = "Boka operation", preliminarbokningLocked = false) => {
        dialogEvents.$emit("openPopup", {
            title,
            component: BokaOperation,
            dismissable: true,
            data: {
                listItem: store.getters["operation/operationsanmalanV2/operationsanmalanById"]({
                    id: data.id,
                }),
                preliminarbokningLocked,
            },
        });
    };

    const onOperationsanmalanUppdaterad = async (anmalan: unknown) => {
        store.commit("operation/operationsanmalan/setOpAnmala", anmalan);
        store.commit("operation/operationsanmalanV2/updateOperationsanmalningar", {
            subsetName: "newlyUpdated",
            operationsanmalningar: [
                {
                    ...(anmalan as OperationsanmalanType),
                    storeInfo: {
                        partial: false,
                    },
                },
            ],
            offset: Object.keys(store.state.operation["operationsanmalanV2"].subsets.newlyUpdated.ids).length,
            frozenSubsets: [subsetName.value],
        });
    };

    const onRefreshTab = async () => {
        filterParams.value = await filterRef?.value?.manualSubmit?.();
        await fetchOperationsanmalningar({ page: 1, forceRefresh: true, resetLocalSubsets: true });
    };

    onMounted(async () => {
        store.dispatch("operation/operationsanmalanV2/addSubscriber");

        // TODO Alla emitters nedan ska fasas ut med tiden

        emitter.on("operationsanmalan-uppdaterad", onOperationsanmalanUppdaterad);
        emitter.on("operationsanmalan-bokad", onOperationsanmalanUppdaterad);
        emitter.on("operationsanmalan-avbokad", onOperationsanmalanUppdaterad);
        emitter.on(`refreshTab-${tabId}`, onRefreshTab);
    });

    onUnmounted(() => {
        store.dispatch("operation/operationsanmalanV2/removeSubscriber");
        emitter.off("operationsanmalan-uppdaterad", onOperationsanmalanUppdaterad);
        emitter.off("operationsanmalan-bokad", onOperationsanmalanUppdaterad);
        emitter.off("operationsanmalan-avbokad", onOperationsanmalanUppdaterad);
        emitter.off(`refreshTab-${tabId}`);
    });

    const onFilter = ({ value }: { value: any }) => {
        filterParams.value = value;
        fetchOperationsanmalningar({ page: 1 });
    };

    const onPage = ({ page, rows }: { page: number; rows: number }) => {
        listLimit.value = rows;

        fetchOperationsanmalningar({ page, limit: rows });
    };

    const onSort = async ({ sortField, sortOrder }: { sortField: string; sortOrder: number }) => {
        if (sortField) {
            filterRef?.value?.setFieldValue?.({ name: "ordering", value: `${sortOrder > 0 ? "" : "-"}${sortField}` });
        } else {
            filterRef?.value?.setFieldValue?.({ name: "ordering", value: null });
        }

        const data = await filterRef?.value?.manualSubmit?.();

        if (data) {
            onFilter({ value: data });
        }
    };
</script>

<template>
    <div class="operationskoordinering">
        <div class="header">
            <h2>Operationskoordinering</h2>
            <KallelserSmsButton />
        </div>
        <OperationskoordineringFilter
            ref="filterRef"
            tableStateName="operationskoordinering-table"
            :keepOnClear="['show_strukna', 'ordering']"
            :filterOnMount="!!initialFilterParams"
            :defaultValue="filterParams"
            @submit="onFilter"
        />
        <KTable
            reorderableColumns
            togglableColumns
            paginator
            alwaysShowPaginator
            useState
            loadingMessage="Hämtar operationsanmälningar"
            paginatorPosition="bottom"
            name="operationskoordinering-table"
            :columns="columns"
            :actions="dropdownActions"
            :value="operationsanmalningar"
            :totalRecords="listCount"
            :loading="isLoading"
            :unselectable="true"
            :first="first"
            @rowClick="onRowClick"
            @menuClick="onRowClick"
            @page="onPage"
            @sort="onSort"
        >
            <template #header>
                <KCheckbox
                    name="show_strukna"
                    label="Visa strukna"
                    valueType="boolean"
                    :value="initialFilterParams?.show_strukna ?? false"
                    @inputValue="onVisaStrukna"
                />
            </template>
        </KTable>
        <SkickaSomRemiss ref="skickaSomRemissRef" />
    </div>
</template>

<style lang="scss">
    // För att se till att höjden på containern sträcker ut sig korrekt så att tabellen kan anpassa sig efter
    // tillgänglig yta
    #tab-container .tab-containers section:has(.operationskoordinering) {
        > div:first-child {
            height: 100%;
        }
    }
</style>

<style lang="scss" scoped>
    @import "@/style/_variables.scss";
    @import "@/style/container";
    @import "@/style/typography.scss";

    .operationskoordinering {
        width: $tab-wider-width;
        max-width: 100%;
        height: 100%;
        display: flex;
        flex-flow: column;

        .header {
            display: flex;
            align-items: center;
            gap: 1rem;
            margin-bottom: 1.5rem;
            flex-wrap: wrap;

            h2 {
                font-size: $h1-font-size;
                line-height: $h1-line-height;
                margin: 0;
            }
        }

        // För att låta tabellen växa automatiskt utifrån tillgängligt utrymme
        ::v-deep .k-table {
            min-height: 30rem;
        }
    }
</style>
